import { getEquationParts } from "@/models/computedNode/utils";
import { isRelation } from "@/models/utils/semanticChecks";

import { collectPrecedingWrapperIds } from "./collectPrecedingWrapperIds";
import { substituteReferenceNodes } from "./substituteReferenceNodes";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";
import type { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";
import type { NodeJson } from "@corca/data";
import type { Editor } from "@tiptap/core";

/**
 * One math line in scope for a hint. Kept split rather than whole because a table column
 * contributes equations that live in no wrapper of their own: the row header supplies the left
 * side and the column cell the right, with no `=` node between them.
 *
 * @example <caption>A `| x + a | 1 |` table row</caption>
 * { lhs: [x, +, a], rhs: [1] }
 */
export type ScopeExpression = {
  /**
   * Where the line is attributed for dependency tracking (`usedWrapperIds`). A synthesized row
   * equation names its value cell — the part that varies per column.
   */
  wrapperId: string;
  /** Nodes left of the first top-level `=`; `null` when the line is not an equation. */
  lhs: NodeModel[] | null;
  /** Nodes right of that `=`, or the whole line when there is none. */
  rhs: NodeModel[];
};

/** Both sides as one list — the split `=` is not among them. */
export function scopeExpressionNodes(expr: ScopeExpression): NodeModel[] {
  return expr.lhs ? [...expr.lhs, ...expr.rhs] : expr.rhs;
}

/** The line as inline children, with its `=` restored and computed references resolved. */
export function scopeExpressionJson(expr: ScopeExpression): NodeJson[] {
  const toJson = (nodes: NodeModel[]) => nodes.map((n) => substituteReferenceNodes(n.toJSON()));
  if (!expr.lhs) return toJson(expr.rhs);
  return [...toJson(expr.lhs), makeEqualsRelationJson(), ...toJson(expr.rhs)];
}

/** Split a line at its first top-level `=`. */
export function toScopeExpression(wrapperId: string, nodes: NodeModel[]): ScopeExpression {
  const parts = getEquationParts(nodes);
  if (parts.length === 0) return { wrapperId, lhs: null, rhs: nodes };
  const [lhs, rhs] = parts;
  return { wrapperId, lhs, rhs };
}

/** Null when the wrapper holds no expression. */
export function wrapperScopeExpression(wrapper: WrapperNodeModel): ScopeExpression | null {
  const nodes = wrapper.inlineNode?.nodes;
  if (!nodes?.length) return null;
  return toScopeExpression(wrapper.id, nodes);
}

/** Scope for the given wrapper ids, in that order; defaults to every wrapper in the document. */
export function wrapperScope(
  wrapperNodes: WrapperNodeModel[],
  wrapperIds?: string[],
): ScopeExpression[] {
  if (!wrapperIds) return wrapperNodes.map(wrapperScopeExpression).filter(Boolean);

  const byId = new Map(wrapperNodes.map((w) => [w.id, w]));
  return wrapperIds
    .map((id) => {
      const wrapper = byId.get(id);
      return wrapper ? wrapperScopeExpression(wrapper) : null;
    })
    .filter(Boolean);
}

/** The document lines in scope before `pos` (see `collectPrecedingWrapperIds` for the rules). */
export function documentScope(doc: DocumentModel, tiptap: Editor, pos: number): ScopeExpression[] {
  return wrapperScope(doc.textNode.wrapperNodes, collectPrecedingWrapperIds(tiptap, pos));
}

/** Relation operators on the line, counting the `=` it was split at. */
export function countRelations(expr: ScopeExpression): number {
  return scopeExpressionNodes(expr).filter(isRelation).length + (expr.lhs ? 1 : 0);
}

/** True when the line carries a relation beyond its `=` (`<`, `∈`, …). */
export function hasNonEqualRelation(expr: ScopeExpression): boolean {
  return scopeExpressionNodes(expr).some(isRelation);
}

export function makeEqualsRelationJson(): NodeJson {
  return {
    type: "operatorSymbol",
    props: {
      name: "Equal",
      semanticType: ["relation"],
    },
    children: [
      {
        type: "symbol",
        props: {
          key: "symbol-equal",
          value: "=",
        },
        children: [],
      },
    ],
  };
}
